import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { withFormsy } from 'formsy-react';
import { Autocomplete, CircularProgress, styled } from '@mui/material';

import { closeDialog } from 'app/store/fuse/dialogSlice';
import useCanOpenPage from 'app/providers/acl/useCanOpenPage';

import BaseTextField, { type FieldProps } from './components/BaseTextField';
import useSettings from '../hooks/useSettings';
import Icon from '../Icon';

import useModuleSearch, { type UseModuleSearchProps } from './components/useModuleSearch';
import { ModuleSearchPopupIndicator } from './components/ModuleSearchComponents';
// eslint-disable-next-line import/no-cycle
import { showModuleSearchFieldDialog } from './components/ModuleSearchFieldDialog';

export type ModuleSearchFieldProps = Omit<FieldProps<any>, 'startAdornment' | 'endAdornment'> &
	Omit<UseModuleSearchProps, 'sendChange'>;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
	'& > .MuiTextField-root > .MuiInputBase-root': {
		'&.Mui-disabled': {
			paddingRight: 8
		},
		'& > .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
			margin: 0,
			paddingLeft: 4,
			paddingRight: 4
		}
	}
}));

/** A SearchSelectField specific to modules */
function ModuleSearchField(props: ModuleSearchFieldProps) {
	const dispatch = useDispatch<any>();
	const { settings, t } = useSettings(props.module);
	const { canOpenDetailsPage } = useCanOpenPage();

	const isInsideAnotherDialog = useSelector(({ fuse }: any) => fuse.moduleSearchDialog.state);

	const hasLogs = props.logProps?.hasLogs(props.name);
	const link = canOpenDetailsPage(props.module, props.uid ?? undefined);

	const sendChange = newValue => props.sendChange(props.name, newValue);

	const {
		options,
		isLoading,
		PaperComponent,
		isOptionEqualToValue,
		parseOptionLabel,
		renderOption,
		handleInputChange,
		handleChange,
		handleKeyDown
	} = useModuleSearch({ ...props, sendChange });

	function handleFieldChange(newValue: any) {
		props.beforeChange?.(newValue);
		handleChange(newValue);
		props.afterChange?.(newValue);
	}

	const handleOpenDialog = () =>
		dispatch(
			showModuleSearchFieldDialog({
				label: props.label,
				module: props.module,
				filters: props.filters,
				value: props.value,
				sendChange: handleFieldChange,
				parseOptionLabel
			})
		);

	const startAdornment = isLoading ? (
		<CircularProgress size={20} color="inherit" disableShrink />
	) : props.value?.[`${settings.modu_short}_deleted`] ? (
		<Icon icon="delete-alert" size={20} color="warning.main" />
	) : (
		<Icon icon={settings.modu_icon} size={20} color={props.value ? 'action.active' : 'text.disabled'} />
	);

	const linkAdornment =
		!!props.value && !props.value[`${settings.modu_short}_deleted`] && !!link ? (
			<div>
				<Link to={link || '/404'} style={{ color: 'inherit' }}>
					<Icon icon="open-in-new" size={20} onClick={() => dispatch(closeDialog())} />
				</Link>
			</div>
		) : undefined;

	const PopupIndicator = React.forwardRef(() => ModuleSearchPopupIndicator({ handleOpenDialog }));

	return (
		<StyledAutocomplete
			className={props.className}
			renderInput={params => (
				<BaseTextField
					{...params}
					name={props.name}
					label={props.label}
					InputProps={props.isEdit ? params.InputProps : {}}
					isEdit={props.isEdit}
					startAdornment={startAdornment}
					endAdornment={props.isEdit ? params.InputProps.endAdornment : linkAdornment}
					required={props.required}
					errorMessage={props.errorMessage}
					isFormSubmitted={props.isFormSubmitted}
					isPristine={props.isPristine}
					isValid={props.isValid}
					logProps={props.logProps}
				/>
			)}
			slotProps={{
				paper: { component: PaperComponent },
				popupIndicator: { component: PopupIndicator }
			}}
			options={options}
			value={props.value || null}
			onInputChange={handleInputChange}
			onChange={(_event, newValue) => handleFieldChange(newValue)}
			onKeyDown={handleKeyDown}
			disabled={!props.isEdit || hasLogs}
			loading={isLoading}
			loadingText={t('LOADING')}
			noOptionsText={t('LIST_NO_ITEMS')}
			filterOptions={() => options}
			isOptionEqualToValue={isOptionEqualToValue}
			getOptionLabel={parseOptionLabel}
			renderOption={renderOption}
			selectOnFocus
			disableClearable={!props.isEdit}
			forcePopupIcon={props.isEdit && !isInsideAnotherDialog}
			// freeSolo
		/>
	);
}

export default React.memo(withFormsy(ModuleSearchField));
